import * as THREE from 'three'
import {
  SV_GRAVITY,
  SV_ACCELERATE,
  SV_AIRACCELERATE,
  SV_FRICTION,
  SV_STOPSPEED,
  SV_MAXSPEED,
  SV_MAXVELOCITY,
  SV_AIRSPEED_CAP,
  JUMP_IMPULSE,
  PLAYER_RADIUS,
  WALKABLE_NORMAL_Y,
  MAX_CLIP_ITERATIONS,
  NOCLIP_SPEED,
  NOCLIP_BOOST_MULT,
} from './constants'
import type { InputState } from './input'
import type { CollisionWorld } from './collision'

// Plain serializable simulation state (snapshot/network-friendly).
export interface PlayerState {
  tick: number
  position: THREE.Vector3
  velocity: THREE.Vector3
  onGround: boolean
  groundNormal: THREE.Vector3
  noclip: boolean // singleplayer-only; server MUST reject noclip=true in MP (Phase 2)
}

export function makePlayerState(spawn: THREE.Vector3): PlayerState {
  return {
    tick: 0,
    position: spawn.clone(),
    velocity: new THREE.Vector3(),
    onGround: false,
    groundNormal: new THREE.Vector3(0, 1, 0),
    noclip: false,
  }
}

export function respawnPlayer(s: PlayerState, spawn: THREE.Vector3): void {
  s.position.copy(spawn)
  s.velocity.set(0, 0, 0)
  s.onGround = false
  s.groundNormal.set(0, 1, 0)
}

// Pure simulation step: mutates `s` from input + world over `dt`.
// Keep this function self-contained — every code path here must be portable to Go.
export function simulatePlayer(
  s: PlayerState,
  input: InputState,
  world: CollisionWorld,
  dt: number,
): void {
  s.tick++

  if (input.noclipPressed) {
    s.noclip = !s.noclip
    s.velocity.set(0, 0, 0)
    s.onGround = false
  }

  if (s.noclip) {
    simulateNoclip(s, input, dt)
    return
  }

  const sy = Math.sin(input.yaw)
  const cy = Math.cos(input.yaw)
  const fwdX = -sy, fwdZ = -cy
  const rgtX =  cy, rgtZ = -sy

  let wishX = 0, wishZ = 0
  if (input.forward) { wishX += fwdX; wishZ += fwdZ }
  if (input.back)    { wishX -= fwdX; wishZ -= fwdZ }
  if (input.right)   { wishX += rgtX; wishZ += rgtZ }
  if (input.left)    { wishX -= rgtX; wishZ -= rgtZ }

  const wishLen = Math.hypot(wishX, wishZ)
  let wishspeed = 0
  if (wishLen > 0) {
    wishX /= wishLen
    wishZ /= wishLen
    wishspeed = SV_MAXSPEED
  }

  categorizeGround(s, world)

  if (s.onGround && input.jumpPressed) {
    s.velocity.y = JUMP_IMPULSE
    s.onGround = false
  }

  if (s.onGround) {
    applyFriction(s, dt)
    accelerate(s, wishX, wishZ, wishspeed, SV_ACCELERATE, dt)
  } else {
    airAccelerate(s, wishX, wishZ, wishspeed, SV_AIRACCELERATE, dt)
    s.velocity.y -= SV_GRAVITY * dt
  }

  clampVelocity(s)
  stepMove(s, world, dt)
}

function categorizeGround(s: PlayerState, world: CollisionWorld): void {
  const probe = world.raycast(s.position, DOWN, PLAYER_RADIUS + 2)
  if (probe && probe.normal.y >= WALKABLE_NORMAL_Y && s.velocity.y <= 0.1) {
    s.onGround = true
    s.groundNormal.copy(probe.normal)
  } else {
    s.onGround = false
  }
}
const DOWN = new THREE.Vector3(0, -1, 0)

function applyFriction(s: PlayerState, dt: number): void {
  const vx = s.velocity.x, vz = s.velocity.z
  const speed = Math.hypot(vx, vz)
  if (speed < 0.1) {
    s.velocity.x = 0
    s.velocity.z = 0
    return
  }
  const control = speed < SV_STOPSPEED ? SV_STOPSPEED : speed
  const drop = control * SV_FRICTION * dt
  const newspeed = Math.max(0, speed - drop)
  const scale = newspeed / speed
  s.velocity.x = vx * scale
  s.velocity.z = vz * scale
}

function accelerate(
  s: PlayerState,
  wishX: number, wishZ: number, wishspeed: number, accel: number, dt: number,
): void {
  if (wishspeed === 0) return
  const currentspeed = s.velocity.x * wishX + s.velocity.z * wishZ
  const addspeed = wishspeed - currentspeed
  if (addspeed <= 0) return
  let accelspeed = accel * dt * wishspeed
  if (accelspeed > addspeed) accelspeed = addspeed
  s.velocity.x += accelspeed * wishX
  s.velocity.z += accelspeed * wishZ
}

// Source PM_AirAccelerate: addspeed uses wishspeed *capped*, accelspeed uses raw wishspeed.
// This is the math that enables surf air-strafe.
function airAccelerate(
  s: PlayerState,
  wishX: number, wishZ: number, wishspeed: number, accel: number, dt: number,
): void {
  if (wishspeed === 0) return
  const wishspeedCapped = wishspeed > SV_AIRSPEED_CAP ? SV_AIRSPEED_CAP : wishspeed
  const currentspeed = s.velocity.x * wishX + s.velocity.z * wishZ
  const addspeed = wishspeedCapped - currentspeed
  if (addspeed <= 0) return
  let accelspeed = accel * dt * wishspeed
  if (accelspeed > addspeed) accelspeed = addspeed
  s.velocity.x += accelspeed * wishX
  s.velocity.z += accelspeed * wishZ
}

function clampVelocity(s: PlayerState): void {
  const v = s.velocity
  if (v.x >  SV_MAXVELOCITY) v.x =  SV_MAXVELOCITY
  if (v.x < -SV_MAXVELOCITY) v.x = -SV_MAXVELOCITY
  if (v.y >  SV_MAXVELOCITY) v.y =  SV_MAXVELOCITY
  if (v.y < -SV_MAXVELOCITY) v.y = -SV_MAXVELOCITY
  if (v.z >  SV_MAXVELOCITY) v.z =  SV_MAXVELOCITY
  if (v.z < -SV_MAXVELOCITY) v.z = -SV_MAXVELOCITY
}

function simulateNoclip(s: PlayerState, input: InputState, dt: number): void {
  const sy = Math.sin(input.yaw), cy = Math.cos(input.yaw)
  const sp = Math.sin(input.pitch), cp = Math.cos(input.pitch)
  const fwdX = -sy * cp, fwdY = sp, fwdZ = -cy * cp
  const rgtX =  cy,                  rgtZ = -sy

  let wishX = 0, wishY = 0, wishZ = 0
  if (input.forward) { wishX += fwdX; wishY += fwdY; wishZ += fwdZ }
  if (input.back)    { wishX -= fwdX; wishY -= fwdY; wishZ -= fwdZ }
  if (input.right)   { wishX += rgtX; wishZ += rgtZ }
  if (input.left)    { wishX -= rgtX; wishZ -= rgtZ }
  if (input.jump)    { wishY += 1 }
  if (input.crouch)  { wishY -= 1 }

  const len = Math.hypot(wishX, wishY, wishZ)
  if (len > 0) {
    const speed = NOCLIP_SPEED * (input.boost ? NOCLIP_BOOST_MULT : 1)
    const scale = speed / len
    s.velocity.x = wishX * scale
    s.velocity.y = wishY * scale
    s.velocity.z = wishZ * scale
  } else {
    s.velocity.set(0, 0, 0)
  }

  s.position.x += s.velocity.x * dt
  s.position.y += s.velocity.y * dt
  s.position.z += s.velocity.z * dt
  s.onGround = false
}

// Discrete move + depenetration with velocity clipping along contact normals.
// At 100Hz and SV_MAXVELOCITY=3500, max per-tick travel = 35 < 2*PLAYER_RADIUS = 36, so no tunneling.
function stepMove(s: PlayerState, world: CollisionWorld, dt: number): void {
  s.position.x += s.velocity.x * dt
  s.position.y += s.velocity.y * dt
  s.position.z += s.velocity.z * dt

  for (let i = 0; i < MAX_CLIP_ITERATIONS; i++) {
    const c = world.depenetrateSphere(s.position, PLAYER_RADIUS)
    if (!c) break
    s.position.add(c.push)
    const into = s.velocity.dot(c.normal)
    if (into < 0) {
      s.velocity.x -= c.normal.x * into
      s.velocity.y -= c.normal.y * into
      s.velocity.z -= c.normal.z * into
    }
    if (c.deepest < 0.001) break
  }
}
